// What the screen says after an approve or a schedule (owner, 2026-10-15).
//
// #WA-510802: « Approuver et envoyer » at 01:08 said « part dans 5 minutes »;
// the night hold kept it until 08:00 and nothing said so. The worker now
// answers what will happen (worker lib/previewSendPlan.ts) and every scheduled
// message carries its state (worker lib/scheduledSend.ts listScheduled). These
// turn those answers into the words on the screen. Pure.
//
// Guard: src/lib/sendVisibility.test.ts
import { casaParts, pad2 } from './scheduleTime';

export type PreviewSends = 'now' | 'at_08' | 'waits_for_pages' | 'blocked';

export interface PreviewPlanLike {
  sends: PreviewSends;
  code?: string;
  reason: string;
  at: string | null;
}

/** « 08:00 », Morocco time. */
export function casaHm(iso: string | null | undefined): string {
  const t = Date.parse(String(iso ?? ''));
  if (!Number.isFinite(t)) return '?';
  const p = casaParts(t);
  return `${pad2(p.hh)}:${pad2(p.mm)}`;
}

const dot = (s: string) => (/[.!?…]$/.test(s) ? s : `${s}.`);

/** The one line after an approval. `null` = a worker too old to say — no promise is made. */
export function previewPlanText(p: PreviewPlanLike | null | undefined, t: (s: string) => string): string {
  if (!p || !p.sends) return t('Approuvé.');
  switch (p.sends) {
    case 'now': return t('Part au client dans les 5 minutes.');
    case 'at_08': return `${t('En file — part à')} ${casaHm(p.at)} ${t('(heure calme).')}`;
    case 'waits_for_pages':
      return p.at
        ? `${t('Part dès que les pages 3D sont prêtes — pas avant')} ${casaHm(p.at)} ${t('(heure calme).')}`
        : t('Part dès que les pages 3D sont prêtes.');
    default:
      return p.code === 'already_sent'
        ? dot(`${t('Déjà chez la cliente —')} ${p.reason}`)
        : dot(`${t('Ne partira pas tout seul —')} ${p.reason}`);
  }
}

/** Is this plan bad news (the screen colours it)? */
export const planIsHeld = (p: PreviewPlanLike | null | undefined) => Boolean(p && (p.sends === 'at_08' || (p.sends === 'blocked' && p.code !== 'already_sent')));

export type ScheduledStateName = 'waiting' | 'sending' | 'retrying' | 'late' | 'sent' | 'held' | 'failed' | 'cancelled';

export interface ScheduledLike {
  send_after: string;
  sent_at?: string | null;
  state?: ScheduledStateName;
  why?: string | null;
}

export interface ScheduledChip {
  tone: 'wait' | 'ok' | 'bad' | 'muted';
  label: string;
  why: string | null;
  /** « Envoyer maintenant » / « Annuler » make sense (it has not left). */
  actions: boolean;
}

export function scheduledChip(
  r: ScheduledLike, nowMs: number, when: (ms: number, nowMs: number) => string, t: (s: string) => string,
): ScheduledChip {
  const at = Date.parse(r.send_after);
  // A worker from before 2026-10-15 sends only queued rows, with no state.
  const state: ScheduledStateName = r.state ?? (at <= nowMs ? 'sending' : 'waiting');
  const why = r.why ?? null;
  switch (state) {
    case 'waiting': return { tone: 'wait', label: `${t('Programmé')} · ${when(at, nowMs)}`, why, actions: true };
    case 'sending': return { tone: 'wait', label: t('En train de partir…'), why, actions: false };
    case 'retrying': return { tone: 'bad', label: t('Échec — nouvel essai'), why, actions: false };
    case 'late': return { tone: 'bad', label: `${t('En retard — prévu')} ${when(at, nowMs)}`, why, actions: true };
    case 'sent': return { tone: 'ok', label: `${t('Envoyé')} · ${when(Date.parse(r.sent_at ?? r.send_after), nowMs)}`, why: null, actions: false };
    case 'held': return { tone: 'bad', label: t('Retenu'), why, actions: false };
    case 'failed': return { tone: 'bad', label: t('Pas envoyé'), why, actions: false };
    case 'cancelled': return { tone: 'muted', label: t('Annulé'), why, actions: false };
  }
}

/** The time the server SAVED (its answer), else the one picked. */
export function savedScheduleMs(res: unknown, pickedMs: number): number {
  const iso = (res as { scheduled?: { send_at?: unknown } } | null | undefined)?.scheduled?.send_at;
  const t = typeof iso === 'string' ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : pickedMs;
}
